import fondoUrl from "../../assets/invitation/fondo-pluma.webp";
import { API_BASE_URL } from "../../config/apiConfig";

/**
 * Generación de la invitación (carta de pergamino) en el navegador.
 *
 * Componemos sobre un <canvas>:
 *   1. El fondo: fotografía de pergamino con pluma de tinta e tintero
 *      (pluma arriba-izquierda, tintero arriba-derecha; el tercio inferior
 *      del pergamino queda libre para el contenido).
 *   2. La foto de perfil "pegada" como una fotografía antigua (marco, sombra,
 *      tono sepia y esquinas), a la izquierda de la zona libre.
 *   3. El nombre del participante en tipografía manuscrita (auto-ajustado),
 *      en el centro.
 *   4. El nombre de usuario / @handle (opcional), bajo el nombre.
 *   5. El logo del concurso como emblema, a la derecha.
 *
 * El resultado es un dataURL PNG.
 */

// Ruta pública del logo del concurso (mismo origen -> no contamina el canvas).
const LOGO_URL = `${process.env.PUBLIC_URL || ""}/media/images/iconobarba.png`;

// Coordenadas en el espacio del fondo (imagen nativa 612 x 408, apaisada).
// La pluma ocupa el borde superior y el tintero la esquina superior derecha,
// así que el contenido se coloca en el pergamino limpio:
//   - foto tipo Polaroid a la izquierda (con el @ manuscrito en su banda blanca),
//   - nombre manuscrito en el centro,
//   - logo pequeño como sello en la esquina inferior derecha.
const LAYOUT = {
  width: 612,
  height: 408,
  scale: 3, // exportar a 3x (1836 x 1224); el texto se mantiene nítido
  foto: {
    cx: 150,
    cy: 256,
    size: 140, // lado de la ventana (cuadrada) de la foto
    border: 11, // margen blanco superior / laterales
    bottomBorder: 46, // margen blanco inferior (mayor, estilo Polaroid) -> aquí va el @
    rot: -0.03,
    // Rótulo (@handle) escrito a mano sobre la banda blanca inferior.
    caption: {
      maxWidth: 128,
      maxHeight: 36,
      maxFont: 30,
      minFont: 11,
      lineHeight: 1,
      family: "'Segoe Script', 'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', 'Dancing Script', cursive",
      weight: "400",
      style: "normal",
      color: "#2f2617",
      shadowColor: "rgba(0,0,0,0.12)",
      shadowBlur: 1,
      shadowOffsetY: 0.5,
    },
  },
  logo: { cx: 560, cy: 352, maxWidth: 68, maxHeight: 84 },
  nombre: {
    cx: 352,
    cy: 250,
    maxWidth: 220,
    maxHeight: 130,
    maxFont: 58,
    minFont: 18,
    lineHeight: 0.98,
    family: "'Segoe Script', 'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', 'Dancing Script', cursive",
    weight: "400",
    style: "normal",
    color: "#3a2711",
    shadowColor: "rgba(70,45,15,0.35)",
    shadowBlur: 3,
    shadowOffsetY: 1.5,
  },
};

/** Carga una imagen y resuelve cuando está lista para dibujar. */
function cargarImagen(src, crossOrigin) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("No se pudo cargar la imagen: " + src));
    img.decoding = "async";
    img.src = src;
  });
}

/**
 * Carga la foto de perfil garantizando que el canvas NO quede "tainted".
 * - data:/blob: se cargan directamente.
 * - Cualquier URL http(s) se pasa por el proxy del backend (CORS), que devuelve
 *   los bytes; los leemos como blob y creamos un objectURL local para dibujar
 *   sin contaminar el canvas.
 */
async function cargarFotoSegura(imagenSrc) {
  const src = String(imagenSrc || "").trim();
  if (!src) throw new Error("No hay foto de perfil disponible.");

  if (src.startsWith("data:") || src.startsWith("blob:")) {
    const img = await cargarImagen(src);
    return { img, cleanup: () => {} };
  }

  const proxyUrl = `${API_BASE_URL}/proxy_avatar.php?url=${encodeURIComponent(src)}`;
  const res = await fetch(proxyUrl, { credentials: "omit" });
  if (!res.ok) {
    throw new Error("No se pudo obtener la foto de perfil (proxy).");
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await cargarImagen(objectUrl);
    return { img, cleanup: () => URL.revokeObjectURL(objectUrl) };
  } catch (err) {
    URL.revokeObjectURL(objectUrl);
    throw err;
  }
}

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/** Dibuja una imagen cubriendo (object-fit: cover) un rectángulo. */
function drawCover(ctx, img, x, y, w, h) {
  const scale = Math.max(w / img.width, h / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
}

/** Dibuja una imagen contenida (object-fit: contain) y centrada en una caja. */
function dibujarContenido(ctx, img, { cx, cy, maxWidth, maxHeight }) {
  if (!img || !img.width || !img.height) return;
  const scale = Math.min(maxWidth / img.width, maxHeight / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  ctx.save();
  ctx.shadowColor = "rgba(50,32,10,0.4)";
  ctx.shadowBlur = 6;
  ctx.shadowOffsetY = 3;
  ctx.drawImage(img, cx - dw / 2, cy - dh / 2, dw, dh);
  ctx.restore();
}

/**
 * Dibuja la foto como una fotografía instantánea (Polaroid): cartulina blanca
 * con margen fino arriba/laterales y una banda blanca mayor abajo, donde se
 * escribe a mano el @ del participante.
 */
function dibujarPolaroid(ctx, img, cfg, handle) {
  const { cx, cy, size, border, bottomBorder, rot } = cfg;
  const w = size;
  const h = size;
  const ow = w + border * 2;
  const oh = h + border + bottomBorder;

  const r = size * 0.015; // radio de esquina (sutil)
  const sombra = size * 0.1; // difuminado de la sombra

  // Ventana de la foto (arriba de la cartulina).
  const px = -w / 2;
  const py = -oh / 2 + border;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rot || 0);

  // Sombra + cartulina blanca marfil.
  ctx.save();
  ctx.shadowColor = "rgba(25,15,4,0.5)";
  ctx.shadowBlur = sombra;
  ctx.shadowOffsetX = sombra * 0.2;
  ctx.shadowOffsetY = sombra * 0.45;
  ctx.fillStyle = "#f7f4ea";
  roundRect(ctx, -ow / 2, -oh / 2, ow, oh, r * 2);
  ctx.fill();
  ctx.restore();

  // Foto (recortada) + leve envejecido cálido.
  ctx.save();
  roundRect(ctx, px, py, w, h, r);
  ctx.clip();
  drawCover(ctx, img, px, py, w, h);
  ctx.fillStyle = "rgba(112,74,26,0.12)"; // tono sepia suave
  ctx.fillRect(px, py, w, h);
  const g = ctx.createRadialGradient(0, py + h / 2, w * 0.2, 0, py + h / 2, w * 0.72);
  g.addColorStop(0, "rgba(0,0,0,0)");
  g.addColorStop(1, "rgba(40,24,6,0.28)");
  ctx.fillStyle = g;
  ctx.fillRect(px, py, w, h);
  ctx.restore();

  // Filete fino alrededor de la ventana de la foto.
  ctx.strokeStyle = "rgba(0,0,0,0.18)";
  ctx.lineWidth = Math.max(1, size * 0.006);
  roundRect(ctx, px, py, w, h, r);
  ctx.stroke();

  // @handle manuscrito, centrado en la banda blanca inferior.
  if (handle) {
    const capCenterY = (py + h + oh / 2) / 2;
    dibujarTexto(ctx, handle, { ...cfg.caption, cx: 0, cy: capCenterY });
  }

  ctx.restore();
}

function fijarFuente(ctx, cfg, size) {
  ctx.font = `${cfg.style || "normal"} ${cfg.weight} ${size}px ${cfg.family}`;
}

/** Divide un texto en como máximo 2 líneas equilibradas por palabras. */
function repartirLineas(texto) {
  const palabras = texto.split(/\s+/).filter(Boolean);
  if (palabras.length <= 1) return [texto];
  let mejor = null;
  for (let i = 1; i < palabras.length; i += 1) {
    const l1 = palabras.slice(0, i).join(" ");
    const l2 = palabras.slice(i).join(" ");
    const diff = Math.abs(l1.length - l2.length);
    if (!mejor || diff < mejor.diff) mejor = { diff, lineas: [l1, l2] };
  }
  return mejor ? mejor.lineas : [texto];
}

/** Ajusta el texto (tamaño + líneas) para que quepa en el ancho/alto máximos. */
function ajustarTexto(ctx, texto, cfg) {
  const limpio = String(texto || "").trim().replace(/\s+/g, " ");
  const maxHeight = cfg.maxHeight || Infinity;
  const lh = cfg.lineHeight || 1.1;

  for (let size = cfg.maxFont; size >= cfg.minFont; size -= 2) {
    fijarFuente(ctx, cfg, size);
    if (ctx.measureText(limpio).width <= cfg.maxWidth && size * lh <= maxHeight) {
      return { size, lineas: [limpio] };
    }
  }

  const dosLineas = repartirLineas(limpio);
  if (dosLineas.length === 2) {
    for (let size = cfg.maxFont; size >= cfg.minFont; size -= 2) {
      fijarFuente(ctx, cfg, size);
      const cabeAncho = dosLineas.every((l) => ctx.measureText(l).width <= cfg.maxWidth);
      const cabeAlto = size * lh * 2 <= maxHeight;
      if (cabeAncho && cabeAlto) return { size, lineas: dosLineas };
    }
  }

  return { size: cfg.minFont, lineas: dosLineas.length === 2 ? dosLineas : [limpio] };
}

function dibujarTexto(ctx, texto, cfg) {
  if (!texto) return;
  const { size, lineas } = ajustarTexto(ctx, texto, cfg);
  const lh = (cfg.lineHeight || 1.1) * size;

  fijarFuente(ctx, cfg, size);
  ctx.fillStyle = cfg.color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.shadowColor = cfg.shadowColor || "transparent";
  ctx.shadowBlur = cfg.shadowBlur || 0;
  ctx.shadowOffsetY = cfg.shadowOffsetY || 0;

  const totalAlto = lh * lineas.length;
  const inicioY = cfg.cy - totalAlto / 2 + lh / 2;
  lineas.forEach((linea, i) => {
    ctx.fillText(linea, cfg.cx, inicioY + i * lh);
  });

  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
}

function formatearUsuario(usuario, plataforma) {
  const u = String(usuario || "").trim();
  if (!u) return "";
  if (u.startsWith("@")) return u;
  if (plataforma === "youtube") return u;
  return "@" + u;
}

/**
 * Genera la invitación y devuelve un dataURL PNG.
 * @param {{ nombre: string, imagen: string, usuario?: string, plataforma?: string }} datos
 */
export async function generarInvitacion({ nombre, imagen, usuario, plataforma }) {
  if (!nombre || !String(nombre).trim()) {
    throw new Error("Falta el nombre del participante.");
  }

  if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
    try {
      await document.fonts.ready;
    } catch (_) {
      /* noop */
    }
  }

  const [fondo, foto, logo] = await Promise.all([
    cargarImagen(fondoUrl),
    cargarFotoSegura(imagen),
    cargarImagen(LOGO_URL).catch(() => null), // logo opcional
  ]);

  const { width, height, scale } = LAYOUT;
  const canvas = document.createElement("canvas");
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext("2d");
  ctx.scale(scale, scale);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  try {
    // 1. Fondo: pergamino con pluma e tintero.
    ctx.drawImage(fondo, 0, 0, width, height);

    // 2. Foto tipo Polaroid (con el @ manuscrito en su banda blanca inferior).
    const handle = formatearUsuario(usuario, plataforma);
    dibujarPolaroid(ctx, foto.img, LAYOUT.foto, handle);

    // 3. Nombre manuscrito (centro).
    dibujarTexto(ctx, nombre, LAYOUT.nombre);

    // 4. Logo pequeño como sello (esquina inferior derecha).
    if (logo) dibujarContenido(ctx, logo, LAYOUT.logo);

    return canvas.toDataURL("image/png");
  } finally {
    foto.cleanup();
  }
}

export default generarInvitacion;
